import { r, Connection } from "rethinkdb-ts";
import {
  RETHINKDB_DB,
  RETHINKDB_HOST,
  RETHINKDB_PORT,
} from "@stoqey/common-env";

export type Sort = "asc" | "desc";

export interface RethinkDBChange {
  deleted: number;
  errors: number;
  inserted: number;
  replaced: number;
  skipped: number;
  unchanged: number;
}

interface RethinkArgs {
  databaseName: string;
  host: string;
  port: number;
}

export default class RethinkDB {
  db: Connection = null as any;

  databaseName = RETHINKDB_DB;

  private static _instance: RethinkDB;

  public static get Instance(): RethinkDB {
    return this._instance || (this._instance = new this());
  }

  private constructor() {}

  /**
   * init
   */
  public async init(args?: RethinkArgs): Promise<Connection> {
    const {
      host = RETHINKDB_HOST,
      port = RETHINKDB_PORT,
      databaseName = RETHINKDB_DB,
    } = args || {};

    if (this.db) {
      return this.db;
    }

    try {
      this.databaseName = databaseName;

      this.db = await r.connect({
        host,
        port,
      });
    } catch (error) {
      console.log("error connecting to rethinkdb", error);
      if (process.env && process.env.NODE_ENV !== "test") {
        process.exit(1);
      }
    } finally {
      return this.db;
    }
  }

  /**
   * getRethinkDB
   */
  public async getRethinkDB(): Promise<Connection | null> {
    if (this.db) {
      return this.db;
    }
    return null;
  }

  /**
   * getRethinkDB
   */
  public getRethinkDBSync(): Connection {
    return this.db;
  }

  async sortByField(
    table: string,
    fieldName: string,
    direction: Sort,
    limit: number
  ): Promise<any[]> {
    const limitAmt = limit | 10;

    let sortBy;
    r.desc(fieldName); // by default

    if (direction === "asc") {
      sortBy = r.asc(fieldName);
    }
    return (await r
      .db(this.databaseName)
      .table(table)
      .orderBy(sortBy)
      .limit(limitAmt)
      .run(this.db)) as any;
  }

  async findById(table: string, id: string): Promise<any> {
    return (await r
      .db(this.databaseName)
      .table(table)
      .get(id)
      .run(this.db)) as any;
  }

  async deleteById(table: string, id: string): Promise<any> {
    return (await r
      .db(this.databaseName)
      .table(table)
      .get(id)
      .delete()
      .run(this.db)) as any;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async updateById(
    table: string,
    id: string,
    data: any
  ): Promise<RethinkDBChange> {
    return await r
      .db(this.databaseName)
      .table(table)
      .get(id)
      .update({ ...data })
      .run(this.db);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async createById(table: string, data: any): Promise<any> {
    return (await r
      .db(this.databaseName)
      .table(table)
      .insert({ ...data })
      .run(this.db)) as any;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async createMany(table: string, data: any): Promise<any> {
    return (await r
      .db(this.databaseName)
      .table(table)
      .insert(data, { conflict: "replace" })
      .run(this.db)) as any;
  }
}
